import React, { useMemo } from 'react';
import { type Header } from '@tanstack/react-table';
import classNames from 'classnames';

import { Typography } from '../../../Typography';
import { EHeaderCellType } from '../../types/table';
import { ColumnResizer } from '../ColumnResizer';
import { FilterButton } from '../FilterButton';
import { SortButton } from '../SortButton';

import styles from './HeadCell.module.scss';

export interface IHeadCellProps {
    header: Header<any, unknown>;
    children?: React.ReactNode;
    last?: boolean;
    dense?: boolean;
    hideFilterButton?: boolean;
    empty?: boolean;
}

export const HeadCell: React.FC<IHeadCellProps> = ({
    header,
    children,
    last,
    dense,
    hideFilterButton = false,
    empty = false,
}) => {
    const type = header.column.columnDef.meta?.headerCellType || EHeaderCellType.TEXT;

    const showActions = header.column.getCanSort() || header.column.getCanFilter();

    // If size is set in column definition or column can be resized, use calculated size
    // Otherwise, use auto to fill the remaining space if table is not enough wide
    const columnWidth = useMemo(() => {
        if (header.column.columnDef.size) {
            return header.getSize();
        }
        if (header.column.getCanResize()) {
            return header.getSize();
        }
        return 'auto';
    }, [header.column.columnDef.size, header.getSize(), header.column.getCanResize()]);

    return (
        <th
            colSpan={header.colSpan}
            className={classNames(styles.th, styles[`type-${type}`], {
                [styles.last]: !!last,
                [styles.dense]: !!dense,
                [styles.emptySelection]: empty && header.column.id === 'select',
            })}
            style={{
                width: columnWidth,
            }}
        >
            <div className={classNames(styles.thWrapper)}>
                <Typography variant="base12" color="inherit" fontWeight="inherit" style={{ lineHeight: 1 }}>
                    {children}
                </Typography>
                {showActions && (
                    <div className={classNames(styles.actionsWrapper)}>
                        <SortButton header={header} />
                        {!hideFilterButton && <FilterButton header={header} />}
                    </div>
                )}
            </div>
            {header.column.getCanResize() && <ColumnResizer header={header} />}
        </th>
    );
};
